import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, BeziehungKonditionen, KonditionAuswahl,
              KonditionenAnBeziehung, KonditionsFelder, Zuordnung } from "@kundeninfo/api-client";
import { Feld, Modal, ModalFuss, darfAbbrechen, useBeforeUnload, useFormularZustand }
  from "@kundeninfo/ui";
import { Auswahl, Chip, Formular, Hinweis, Optionen } from "@kundeninfo/ui/me-design";
import { formatKondition } from "../format.js";
import { KiKnopf } from "../components/meHuellen.js";
import "./KundeKonditionen.css";

/** Nur die drei Klientenmethoden, die dieser Abschnitt wirklich braucht — so lässt er sich
 *  im Test mit drei vi.fn zeichnen, ohne den ganzen ApiClient nachzubauen (Muster aus
 *  KundeStammdaten.tsx). */
export type KonditionenClient = Pick<ApiClient,
  "listSelectablePaymentTerms" | "getRelationTerms" | "setRelationTerms">;

export type KundeKonditionenProps = {
  client: KonditionenClient;
  /** Die Kundenbeziehungen aus der Akte. Die Konditionen hängen an IHNEN, nicht an der
   *  Firma: Dieselbe Firma kann mit zwei Konzernunternehmen Verschiedenes vereinbart
   *  haben (Spezifikation, Abschnitt „Abweichungen vom Fundament"). */
  relations: Zuordnung[];
  /**
   * Über diesem Abschnitt steht ein FREMDES Panel — das Stammdaten-Formular (Fixrunde 1,
   * 07.09.2026).
   *
   * Der Abschnitt steht unter den Stammdaten und liegt damit hinter deren Panel. Sein
   * „Ändern" ist dort nicht anklickbar, und ein Knopf, den niemand drücken kann, gehört
   * gesperrt — nicht zuletzt, weil in jsdom nichts den Klick abfängt und ein Test sonst
   * hinter das Panel griffe.
   *
   * Wahlfrei, weil es nicht die Sache dieses Abschnitts ist: Er kennt seinen Platz nicht.
   * Wer ihn woanders einhängt, wo kein Panel darüberliegt, lässt sie weg.
   */
  verdeckt?: boolean;
};

/** Alle Feldnamen, die eine 422 dieser Maske nennen kann — die vier aus `validateTerms`. */
type FeldName = "paymentTermId" | "netDays" | "discountPercent" | "discountDays";

/**
 * Die drei Bedienformen. Die XOR-Regel des Servers („Verweis ODER eigene Werte") wird hier
 * zur BEDIENFORM: Man wählt, was gelten soll, statt beides einzutippen und danach zu
 * erfahren, dass das nicht geht. Die 422 auf `paymentTermId` bleibt als Riegel bestehen —
 * sie ist nur nicht mehr der übliche Weg, von ihr zu erfahren.
 */
type Bedienform = "STANDARD" | "EIGEN" | "KEINE";

/**
 * Der offene Ändern-Dialog. `version` ist die Versionsbasis der KUNDENBEZIEHUNG, mit der
 * gespeichert wird — sie kommt beim Öffnen aus dem zuletzt bekannten Stand und rückt nach
 * einem Konflikt bewusst nach (siehe eigeneEingabenBehalten).
 *
 * Die drei Zahlenfelder stehen als Zeichenketten da, damit ein geleertes Feld erst beim
 * Absenden zu `null` wird — sonst liesse sich eine einmal eingetragene Zahl nicht mehr
 * entfernen.
 */
type DialogStand = {
  relationId: string;
  /** Für die Überschrift des Dialogs: Kürzel und Name des Konzernunternehmens. */
  titel: string;
  version: number;
  form: Bedienform;
  paymentTermId: string;
  netDays: string;
  discountPercent: string;
  discountDays: string;
};

/**
 * Ob zwei Dialogstände dieselben EINGABEN tragen. Bewusst ohne `version` und `titel`:
 * Die Versionsbasis rückt nach einem Konflikt nach (siehe eigeneEingabenBehalten), ohne
 * dass der Benutzer etwas getippt hätte — sie als Änderung zu zählen, machte aus einer
 * Konfliktauflösung einen ungespeicherten Stand.
 */
function eingabenGleich(a: DialogStand, b: DialogStand): boolean {
  return a.form === b.form && a.paymentTermId === b.paymentTermId
    && a.netDays === b.netDays && a.discountPercent === b.discountPercent
    && a.discountDays === b.discountDays;
}

type Konflikt = { von: string; wann: string; fremd: BeziehungKonditionen | null };

const LEERE_EINGABEN = { paymentTermId: "", netDays: "", discountPercent: "", discountDays: "" };

/**
 * Welche Bedienform ein eingetragener Stand ist. Genau die Fallunterscheidung, die auch
 * `resolvePaymentTerm` in `domain` trifft — nur ohne die Werte aufzulösen: Ein Verweis ist
 * ein Verweis, ein eigener Wert sind eigene Werte, nichts ist nichts.
 */
function formAus(terms: BeziehungKonditionen): Bedienform {
  if (terms.paymentTermId !== null) return "STANDARD";
  if (terms.netDays !== null || terms.discountPercent !== null
    || terms.discountDays !== null) return "EIGEN";
  return "KEINE";
}

/** Eine Zahl als Feldwert — leer bleibt leer, damit ein nicht vereinbarter Wert nicht als
 *  „0" im Feld steht und beim nächsten Speichern eine Null vereinbarte. */
function alsFeldwert(zahl: number | null): string {
  return zahl === null ? "" : String(zahl);
}

function standAus(
  relationId: string, titel: string, stand: KonditionenAnBeziehung,
): DialogStand {
  return {
    relationId,
    titel,
    version: stand.terms.version,
    form: formAus(stand.terms),
    paymentTermId: stand.terms.paymentTermId ?? "",
    netDays: alsFeldwert(stand.terms.netDays),
    discountPercent: alsFeldwert(stand.terms.discountPercent),
    discountDays: alsFeldwert(stand.terms.discountDays),
  };
}

/**
 * Eine Eingabe als Zahl — oder `"KEINE_ZAHL"`, wenn dort etwas steht, das keine ist.
 *
 * Das ist KEINE zweite Fachprüfung neben `validateTerms` (Bereiche, Nachkommastellen und
 * die XOR-Regel bleiben ausschliesslich dort): Es ist die Umwandlung von Text in Zahl, und
 * die kann scheitern. Ohne diese Stelle ginge aus „abc" ein `NaN` hinaus, das
 * `JSON.stringify` still zu `null` macht — die Maske meldete nichts, und statt einer
 * Fehlermeldung stünde hinterher „keine Kondition vereinbart" da.
 */
const KEINE_ZAHL = Symbol("keine Zahl");

function alsZahl(eingabe: string): number | null | typeof KEINE_ZAHL {
  const roh = eingabe.trim();
  if (roh === "") return null;
  // Komma als Dezimaltrennzeichen ist die deutsche Schreibweise; `Number` kennt nur den Punkt.
  const zahl = Number(roh.replace(",", "."));
  return Number.isFinite(zahl) ? zahl : KEINE_ZAHL;
}

/**
 * `conflict.current` kommt als unknown über die Leitung (die API kann seine Gestalt im
 * Schema nicht versprechen, siehe openapi.ts). Tatsächlich liefert der Fehlerhandler die
 * volle Kundenbeziehung; hier wird das geprüft statt blind behauptet — fehlt die Gestalt,
 * zeigt der Konfliktkasten nur die Meldung.
 */
function alsBeziehung(wert: unknown): BeziehungKonditionen | null {
  if (typeof wert !== "object" || wert === null) return null;
  const b = wert as Partial<BeziehungKonditionen>;
  if (typeof b.id !== "string" || typeof b.version !== "number") return null;
  return wert as BeziehungKonditionen;
}

const KONFLIKT_ZEIT = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
});

function formatKonfliktZeit(iso: string): string {
  const zeitpunkt = new Date(iso);
  if (Number.isNaN(zeitpunkt.getTime())) return iso;
  return `${KONFLIKT_ZEIT.format(zeitpunkt)} Uhr`;
}

/**
 * Der Abschnitt „Zahlungskonditionen" unter den Stammdaten — je Kundenbeziehung eine Zeile.
 *
 * Angezeigt wird die AUFGELÖSTE Kondition (`resolved`), also das, was tatsächlich gilt:
 * Bei einem Verweis sind das die Werte des Stammsatzes, und eine Änderung am Standard wirkt
 * damit sofort hier durch — genau der Zweck der Konstruktion (Spezifikation). Der
 * eingetragene Stand (`terms`) steht daneben nur als Quellenangabe und ist die Grundlage
 * des Ändern-Dialogs.
 *
 * Die XOR-Regel erscheint als Bedienform, nicht als Fehlermeldung: Wer „Standard aus der
 * Liste" wählt, sieht die eigenen Felder gar nicht mehr, und der Wechsel leert sie. Die
 * 422 des Servers bleibt trotzdem verdrahtet — sie fängt, was diese Maske nicht abfängt
 * (Bereiche, Nachkommastellen, Skonto-Paar).
 */
export function KundeKonditionen(p: KundeKonditionenProps): ReactElement {
  /** Der zuletzt bekannte Stand je Beziehung — Quelle für die Zeile UND für die Version
   *  beim Speichern. Nach einem Erfolg steht hier die ANTWORT mit ihrer neuen Version;
   *  mit der alten scheiterte das nächste Speichern sofort am Versionsvergleich. */
  const [staende, setStaende] = useState<Record<string, KonditionenAnBeziehung>>({});
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [standards, setStandards] = useState<KonditionAuswahl[]>([]);
  const [standardFehler, setStandardFehler] = useState<string | null>(null);

  const [dialog, setDialog] = useState<DialogStand | null>(null);
  const [konflikt, setKonflikt] = useState<Konflikt | null>(null);
  /** „Meine Eingaben behalten“ geht über `formular.neuLaden()` — die Markierung sagt dem
   *  Laden, dass es nur die Versionsbasis vorrückt (siehe KundeStammdaten). */
  const eingabenBehalten = useRef(false);

  const { client, relations } = p;
  const getRelationTerms = client.getRelationTerms;
  const listStandards = client.listSelectablePaymentTerms;
  // Die Kennungen als eine Zeichenkette: Der Ladeeffekt darf nicht bei jedem Neuzeichnen
  // erneut laufen, nur weil `relations` ein neues Feld-Objekt ist.
  const relationIds = relations.map((r) => r.id).join(",");

  /**
   * Formularzustände (ME-Etappe 3, Aufgabe 3): `laeuft` sperrt Felder und Fuß, eine 422 —
   * auch die eigene für eine unlesbare Zahl, siehe `eingabeAus` — legt die Befunde an die
   * Felder und springt zum ersten, eine 409 setzt den „fremden Stand“ (Eingaben bleiben
   * stehen; „Neu laden“ übernimmt den Serverstand, „Meine Eingaben behalten“ rückt nur die
   * Versionsbasis vor).
   */
  const formular = useFormularZustand<KonditionenAnBeziehung>({
    speichern: async () => {
      const d = dialog;
      if (d === null) throw new Error("Kein Dialog offen.");
      try {
        return await client.setRelationTerms(d.relationId, d.version, eingabeAus(d));
      } catch (fehler) {
        if (fehler instanceof ApiError && fehler.status === 409) {
          setKonflikt(fehler.conflict !== undefined
            ? { von: fehler.conflict.changedBy, wann: fehler.conflict.changedAt,
                fremd: alsBeziehung(fehler.conflict.current) }
            : { von: "", wann: "", fremd: null });
        }
        throw fehler;
      }
    },
    neuLaden: async () => {
      const d = dialog;
      const behalten = eingabenBehalten.current;
      eingabenBehalten.current = false;
      if (d === null) return;
      if (behalten) {
        // Ohne mitgelieferten Stand frisch holen und NUR die Version übernehmen — sonst
        // liefe das nächste Speichern sofort wieder in die 409 (Fixrunde 1, H2).
        const version = konflikt?.fremd?.version
          ?? (await client.getRelationTerms(d.relationId)).terms.version;
        setDialog((alt) => (alt === null ? alt : { ...alt, version }));
      } else {
        // Neu geladen wird, weil die 409 nur den eingetragenen Stand trägt; die GELTENDE
        // Kondition dazu kennt nur der Server.
        const frisch = await client.getRelationTerms(d.relationId);
        setStaende((alt) => ({ ...alt, [d.relationId]: frisch }));
        setDialog(standAus(d.relationId, d.titel, frisch));
      }
      setKonflikt(null);
    },
    bekannteFelder: ["paymentTermId", "netDays", "discountPercent", "discountDays"],
  });
  const speichert = formular.laeuft;
  const feldFehler = formular.feldFehler as Partial<Record<FeldName, string>>;

  useEffect(() => {
    const ids = relationIds === "" ? [] : relationIds.split(",");
    if (ids.length === 0) {
      setStaende({});
      return;
    }
    let abgebrochen = false;
    setLadeFehler(null);
    Promise.all(ids.map(async (id) => [id, await getRelationTerms(id)] as const))
      .then((paare) => {
        if (abgebrochen) return;
        setStaende(Object.fromEntries(paare));
      })
      .catch((fehler: unknown) => {
        if (abgebrochen) return;
        setLadeFehler(fehler instanceof ApiError ? fehler.message
          : "Die Zahlungskonditionen konnten nicht geladen werden.");
      });
    return () => { abgebrochen = true; };
  }, [relationIds, getRelationTerms]);

  // Die Standards hängen an keiner Beziehung — sie werden einmal geholt. Scheitert das,
  // bleibt der Abschnitt lesbar und nur die Auswahl „Standard aus der Liste" entfällt; ein
  // Hinweis sagt es, statt stillschweigend eine leere Liste anzubieten (Muster: die Rollen
  // in KundeAnsprechpartner.tsx).
  useEffect(() => {
    let abgebrochen = false;
    listStandards()
      .then((antwort) => { if (!abgebrochen) setStandards(antwort.items); })
      .catch(() => {
        if (!abgebrochen) {
          setStandardFehler("Die gepflegten Zahlungskonditionen konnten nicht geladen "
            + "werden — es lässt sich gerade kein Standard auswählen.");
        }
      });
    return () => { abgebrochen = true; };
  }, [listStandards]);

  // 2a-Kleinpunkt: Der Browser-Standarddialog beim ECHTEN Verlassen (Tab zu, neu laden,
  // App-Wechsel). Verglichen wird mit der Ausgangslage BEIM ÖFFNEN, die sich jederzeit aus
  // dem zuletzt bekannten Stand nachbauen lässt (`standAus` — dieselbe Stelle, aus der
  // `oeffnen` den Dialog befüllt); ein zweiter Zustand daneben ginge sonst mit der Zeit
  // auseinander. Ein bloss geöffneter, unberührter Dialog löst nichts aus.
  const dialogStand = dialog === null ? undefined : staende[dialog.relationId];
  const dialogGeaendert = dialog !== null && dialogStand !== undefined
    && !eingabenGleich(dialog, standAus(dialog.relationId, dialog.titel, dialogStand));
  useBeforeUnload(dialogGeaendert);

  /**
   * Woher die geltende Kondition kommt — die EINE Stelle, die Verweis und eigene Werte
   * auseinanderhält. Sie liest `resolved.source` (aus `resolvePaymentTerm` in `domain`),
   * nicht die eingetragenen Spalten: Die Auflösung entscheidet, nicht die Ansicht.
   */
  function quelle(stand: KonditionenAnBeziehung): string | null {
    if (stand.resolved === null) return null;
    if (stand.resolved.source === "CUSTOM") return "eigene Werte";
    const name = standards.find((s) => s.id === stand.terms.paymentTermId)?.name;
    return name === undefined ? "Standard" : `Standard „${name}“`;
  }

  function oeffnen(r: Zuordnung): void {
    const stand = staende[r.id];
    if (stand === undefined) return;
    formular.zuruecksetzen();
    setKonflikt(null);
    setDialog(standAus(r.id, `${r.shortCode} · ${r.companyName}`, stand));
  }

  /** Das Panel zumachen und alles abräumen, was zu ihm gehörte — der Weg NACH einem
   *  Entscheid (Erfolg oder bestätigtes Abbrechen), nicht der Ausgang selbst. */
  function schliessen(): void {
    setDialog(null);
    formular.zuruecksetzen();
    setKonflikt(null);
  }

  /**
   * Der EINE Ausgang neben dem Speichern (Hausregel „striktes Modalpanel", 06.09.2026):
   * kein Ausgang unter laufendem Schreiben, Rückfrage bei ungespeicherten Eingaben. Knopf
   * und Escape gehen durch diese Funktion — der Rahmen ruft sie ebenso wie die Fußleiste.
   */
  async function abbrechen(): Promise<void> {
    if (speichert) return;
    if (!(await darfAbbrechen(dialogGeaendert))) return;
    schliessen();
  }

  /**
   * Der Wechsel der Bedienform leert die Eingaben der jeweils ANDEREN Formen.
   *
   * Ohne dieses Leeren stünde nach „eigene Werte → Standard aus der Liste" beides im
   * Entwurf, und der Server wiese das mit genau der 422 ab, die diese Bedienform vermeiden
   * soll — der Benutzer sähe einen Fehler für etwas, das er auf dem Bildschirm gar nicht
   * mehr sieht.
   */
  function formWechseln(form: Bedienform): void {
    formular.zuruecksetzen();
    setDialog((d) => (d === null ? d : { ...d, ...LEERE_EINGABEN, form }));
  }

  /** Der Rumpf für den Schreibweg — genau eine der drei Formen ist gefüllt. */
  function eingabeAus(d: DialogStand): KonditionsFelder {
    if (d.form === "KEINE") {
      return { paymentTermId: null, netDays: null, discountPercent: null, discountDays: null };
    }
    if (d.form === "STANDARD") {
      return {
        paymentTermId: d.paymentTermId === "" ? null : d.paymentTermId,
        netDays: null, discountPercent: null, discountDays: null,
      };
    }
    const netDays = alsZahl(d.netDays);
    const discountPercent = alsZahl(d.discountPercent);
    const discountDays = alsZahl(d.discountDays);
    // Eine unlesbare Zahl geht als dieselbe 422-Gestalt in den Formularzustand wie ein
    // Befund des Servers — Fehler am Feld, Sprung zum ersten, kein Aufruf.
    const unlesbar: { field: FeldName; message: string }[] = [];
    if (netDays === KEINE_ZAHL) unlesbar.push({ field: "netDays", message: "Bitte eine Zahl eintragen." });
    if (discountPercent === KEINE_ZAHL) {
      unlesbar.push({ field: "discountPercent", message: "Bitte eine Zahl eintragen." });
    }
    if (discountDays === KEINE_ZAHL) {
      unlesbar.push({ field: "discountDays", message: "Bitte eine Zahl eintragen." });
    }
    if (netDays === KEINE_ZAHL || discountPercent === KEINE_ZAHL
      || discountDays === KEINE_ZAHL) {
      throw new ApiError(422, "EINGABE_UNLESBAR", "Bitte eine Zahl eintragen.", unlesbar);
    }
    return { paymentTermId: null, netDays, discountPercent, discountDays };
  }

  async function speichern(): Promise<void> {
    const d = dialog;
    if (d === null) return;
    const antwort = await formular.aktion();
    if (antwort === undefined) return; // Befund am Feld, im Hinweis oder im Formular
    // Die ANTWORT übernehmen, nicht den eigenen Entwurf: Sie trägt die neue Version
    // (sonst scheiterte das nächste Speichern am Versionsvergleich) und die frisch
    // aufgelöste Kondition.
    setStaende((alt) => ({ ...alt, [d.relationId]: antwort }));
    schliessen();
  }

  /** Konfliktauflösung 2: die eigene Eingabe behalten. Die Versionsbasis rückt auf den
   *  Serverstand vor — das nächste Speichern überschreibt den fremden Stand dann BEWUSST
   *  (der Hinweis hat gesagt, dass es einen gibt). Auflösung 1 ist „Neu laden“. */
  function eigeneEingabenBehalten(): void {
    eingabenBehalten.current = true;
    void formular.neuLaden();
  }

  const waehlbareStandards = dialog === null ? [] : standards.filter(
    (s) => s.active || s.id === dialog.paymentTermId);

  return (
    <section className="kk" aria-label="Zahlungskonditionen">
      <h3 className="kk__titel">Zahlungskonditionen</h3>

      {ladeFehler !== null && <Hinweis art="fehler">{ladeFehler}</Hinweis>}

      {relations.length === 0 && (
        <p className="kk__hinweis">Dieser Kunde ist noch keinem Unternehmen zugeordnet.</p>
      )}

      <ul className="kk__liste">
        {relations.map((r) => {
          const stand = staende[r.id];
          return (
            <li key={r.id} className="kk__zeile">
              <Chip className="kk__chip">{r.shortCode}</Chip>
              {stand === undefined ? (
                <span className="kk__wert">Lädt …</span>
              ) : (
                <>
                  <span className="kk__wert">{formatKondition(stand.resolved)}</span>
                  {quelle(stand) !== null && (
                    <span className="kk__quelle">{quelle(stand)}</span>
                  )}
                </>
              )}
              {/* me-knopf (ME-Etappe 4, P4); der Name je Beziehung über KiKnopf `name`. */}
              <KiKnopf className="kk__knopf"
                name={`Zahlungskondition für ${r.companyName} ändern`}
                deaktiviert={stand === undefined || dialog !== null || p.verdeckt === true}
                onClick={() => oeffnen(r)}>
                Ändern
              </KiKnopf>
            </li>
          );
        })}
      </ul>

      {dialog !== null && (
        <Modal titel={`Zahlungskondition — ${dialog.titel}`} onAbbrechen={abbrechen}
          abbrechenGesperrt={speichert}>
          {/* Der Inhalt trägt seinen eigenen senkrechten Abstand: Der gemeinsame Rahmen
              staffelt seine Kinder nicht, damit jede Maske ihr Raster selbst bestimmt. */}
          {formular.meldung !== null && <Hinweis art="fehler">{formular.meldung}</Hinweis>}
          <Formular beschriftung="oben" {...formular.formularProps}
            hinweis={konflikt === null ? undefined : (
              <span>
                {konflikt.von !== ""
                  ? <>{konflikt.von} hat diese Zahlungskondition inzwischen geändert
                      ({formatKonfliktZeit(konflikt.wann)}).</>
                  : <>Diese Zahlungskondition wurde inzwischen geändert.</>}
                {" "}Deine Eingaben sind nicht verloren — entscheide, welcher Stand gilt:
                „Neu laden“ übernimmt ihn.{" "}
                <KiKnopf className="ki-eingaben-behalten"
                  onClick={eigeneEingabenBehalten}>
                  Meine Eingaben behalten
                </KiKnopf>
              </span>
            )}>
            {/* `Optionen` (me-optionen, Entscheid 40, ME-Etappe 4 P17) statt nativer Radios
                (Übergang P4-3); `laeuft` des Formulars sperrt sie mit, der Fokus bleibt. */}
            <Optionen beschriftung="Was soll gelten?" wert={dialog.form}
              onGeaendert={(d) => formWechseln(d.wert as Bedienform)}>
              <option value="STANDARD">Standard aus der Liste</option>
              <option value="EIGEN">Eigene Werte</option>
              <option value="KEINE">Keine Kondition</option>
            </Optionen>

            {dialog.form === "STANDARD" && standardFehler !== null && (
              <Hinweis art="warnung">{standardFehler}</Hinweis>
            )}
            {dialog.form === "STANDARD" && (
              <Auswahl id="kk-standard" name="kk-standard" beschriftung="Zahlungskondition"
                wert={dialog.paymentTermId}
                {...(feldFehler.paymentTermId !== undefined
                  ? { fehler: feldFehler.paymentTermId } : {})}
                onGeaendert={(e) => setDialog((d) => (d === null ? d
                  : { ...d, paymentTermId: e.wert }))}>
                <option value="">Bitte wählen …</option>
                {waehlbareStandards.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.active ? s.name : `${s.name} (stillgelegt)`}
                  </option>
                ))}
              </Auswahl>
            )}

            {dialog.form === "EIGEN" && (
              <>
                <Feld id="kk-netDays" label="Zahlungsziel (Tage)" bearbeitbar
                  wert={dialog.netDays}
                  onChange={(wert) => setDialog((d) => (d === null ? d : { ...d, netDays: wert }))}
                  {...(feldFehler.netDays !== undefined ? { fehler: feldFehler.netDays } : {})}
                />
                <Feld id="kk-discountPercent" label="Skonto (%)" bearbeitbar
                  wert={dialog.discountPercent}
                  onChange={(wert) => setDialog((d) => (d === null ? d
                    : { ...d, discountPercent: wert }))}
                  {...(feldFehler.discountPercent !== undefined
                    ? { fehler: feldFehler.discountPercent } : {})}
                />
                <Feld id="kk-discountDays" label="Skontofrist (Tage)" bearbeitbar
                  wert={dialog.discountDays}
                  onChange={(wert) => setDialog((d) => (d === null ? d
                    : { ...d, discountDays: wert }))}
                  {...(feldFehler.discountDays !== undefined
                    ? { fehler: feldFehler.discountDays } : {})}
                />
              </>
            )}

            {dialog.form === "KEINE" && (
              <p className="kk__hinweis">
                Für diese Kundenbeziehung gilt dann keine vereinbarte Zahlungskondition.
              </p>
            )}
          </Formular>

          <ModalFuss speichernText="Speichern"
            speichernGesperrt={formular.zustand !== "bereit"}
            abbrechenGesperrt={speichert}
            onSpeichern={() => { void speichern(); }} onAbbrechen={abbrechen} />
        </Modal>
      )}
    </section>
  );
}
